/**
 * Applies the user's theme preference (light / dark / system) by toggling the
 * `.dark` class on <html>, which is what the `dark:` variant and the `.dark`
 * token block in styles.css key off.
 */

import type { UserPreferences } from '@/lib/types/preferences'
import useUserPreferencesStore from '@/store/UserPreferencesStore'

type ThemePreference = UserPreferences['theme']

const DARK_QUERY = '(prefers-color-scheme: dark)'

export function resolveTheme(
  preference: ThemePreference,
  systemPrefersDark: boolean,
): 'light' | 'dark' {
  if (preference === 'system') return systemPrefersDark ? 'dark' : 'light'
  return preference
}

function apply(preference: ThemePreference, systemPrefersDark: boolean) {
  const resolved = resolveTheme(preference, systemPrefersDark)
  document.documentElement.classList.toggle('dark', resolved === 'dark')
}

/**
 * Apply the persisted theme immediately and keep it in sync with preference
 * changes and OS-level changes (for "system"). Call once before first render.
 * Returns an unsubscribe function.
 */
export function initTheme(): () => void {
  const media = window.matchMedia(DARK_QUERY)
  const current = () => useUserPreferencesStore.getState().theme

  apply(current(), media.matches)

  const onMediaChange = (event: MediaQueryListEvent) => {
    if (current() === 'system') apply('system', event.matches)
  }
  media.addEventListener('change', onMediaChange)

  const unsubscribe = useUserPreferencesStore.subscribe((state, prev) => {
    if (state.theme !== prev.theme) apply(state.theme, media.matches)
  })

  return () => {
    media.removeEventListener('change', onMediaChange)
    unsubscribe()
  }
}
